import { Link } from 'react-router-dom'
import type { Monster } from '../game/types'
import { FAMILIES } from '../game/families'
import { GEAR_BY_ID } from '../game/gear'
import { equippedFrame, FRAME_CLASS } from '../game/pass'
import { secondaryTypeOf } from '../game/generator'
import { evolutionsOf, STAGE_INFO, stageOf } from '../game/progression'
import MonsterAvatar from './MonsterAvatar'
import RarityBadge from './RarityBadge'
import StatBar from './StatBar'

interface Props {
  monster: Monster
  compact?: boolean
  reveal?: boolean
}

// Pastilles d'évolution — met en avant la variable posée au mint (0/1/2).
const EVO_LABEL: Record<0 | 1 | 2, { pips: string; title: string }> = {
  0: { pips: '○○', title: "N'évolue pas — garde sa forme de base" },
  1: { pips: '✦○', title: 'Peut évoluer 1 fois (⬆ Évolué)' },
  2: { pips: '✦✦', title: "Peut évoluer 2 fois (⬆ Évolué → ★ Apex)" },
}

export default function MonsterCard({ monster, compact = false, reveal = false }: Props) {
  const fam = FAMILIES[monster.family]
  const evos = evolutionsOf(monster)

  return (
    <Link
      to={`/monster/${monster.id}`}
      className={`block rounded-2xl border bg-panel p-3 transition-transform hover:-translate-y-1 hover:shadow-lg ${reveal ? 'anim-reveal' : ''} ${FRAME_CLASS[equippedFrame() ?? ''] ?? ''}`}
      style={{ borderColor: fam.color + '44', boxShadow: `0 4px 24px ${fam.color}18` }}
    >
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-1">
          <span
            className="rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider"
            style={{ color: fam.color, backgroundColor: fam.color + '1e' }}
          >
            {fam.emoji} {fam.label}
          </span>
          {(() => {
            const sec = secondaryTypeOf(monster)
            return sec ? (
              <span className="text-[11px]" title={`Type secondaire : ${FAMILIES[sec].label}`}>
                {FAMILIES[sec].emoji}
              </span>
            ) : null
          })()}
        </span>
        <span className="flex items-center gap-1">
          {monster.onchain && (
            <span
              className="rounded-full bg-cyber/15 px-1.5 py-0.5 text-[10px] font-bold text-cyber"
              title={`NFT #${monster.tokenId} sur Base Sepolia`}
            >
              ⛓
            </span>
          )}
          {!monster.hatching && (
            <span
              className="rounded-full bg-white/5 px-1.5 py-0.5 text-[10px] font-black tracking-widest text-cyber"
              title={`Évolutions : ${EVO_LABEL[evos].title}`}
            >
              {EVO_LABEL[evos].pips}
            </span>
          )}
          <RarityBadge rarity={monster.rarity} />
        </span>
      </div>

      <div
        className="my-2 flex items-center justify-center rounded-xl py-3"
        style={{ background: `radial-gradient(circle at 50% 60%, ${fam.color}22, transparent 70%)` }}
      >
        <MonsterAvatar monster={monster} size={compact ? 72 : 104} />
      </div>

      <p className="truncate text-center text-sm font-extrabold tracking-wide">
        {monster.name}
        {(monster.level ?? 1) > 1 && (
          <span className="ml-1 text-[10px] font-black text-cyber">
            Nv{monster.level}
            {stageOf(monster.level ?? 1, evos) > 0 &&
              ` ${STAGE_INFO[stageOf(monster.level ?? 1, evos)].badge}`}
          </span>
        )}
        {monster.gear && GEAR_BY_ID[monster.gear] && (
          <span className="ml-1 text-[11px]" title={GEAR_BY_ID[monster.gear].name}>
            {GEAR_BY_ID[monster.gear].emoji}
          </span>
        )}
      </p>

      {monster.hatching && (
        <p className="mt-1 text-center text-[10px] font-bold uppercase tracking-wider text-white/40">
          🥚 éclosion en cours…
        </p>
      )}

      {!compact && !monster.hatching && (
        <div className="mt-2 space-y-1">
          <StatBar label="HP" value={monster.stats.hp} max={520} color="#4ade80" />
          <StatBar label="ATK" value={monster.stats.attack} max={110} color="#f87171" />
          <StatBar label="DEF" value={monster.stats.defense} max={110} color="#60a5fa" />
          <StatBar label="SPD" value={monster.stats.speed} max={110} color="#facc15" />
        </div>
      )}
    </Link>
  )
}
